import { create } from 'zustand';
import { aiChatApi } from '../services/aiChatApi';
import type { Conversation, Message } from '../services/aiChatApi';

interface AiChatState {
  isOpen: boolean;
  conversations: Conversation[];
  currentConversationId: string | null;
  messages: Message[];
  isLoading: boolean;
  isAutocompleteLoading: boolean;
  error: string | null;

  // Chat panel actions
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;

  // Conversation actions
  fetchConversations: () => Promise<void>;
  createConversation: (title?: string) => Promise<void>;
  selectConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  clearCurrentConversation: () => void;

  // Message actions
  sendMessage: (content: string) => Promise<void>;

  // Error actions
  clearError: () => void;
}

export const useAiChatStore = create<AiChatState>((set, get) => ({
  isOpen: false,
  conversations: [],
  currentConversationId: null,
  messages: [],
  isLoading: false,
  isAutocompleteLoading: false,
  error: null,

  toggleChat: () => set((state) => ({ isOpen: !state.isOpen })),
  openChat: () => set({ isOpen: true }),
  closeChat: () => set({ isOpen: false }),

  fetchConversations: async () => {
    try {
      const response = await aiChatApi.listConversations();
      if (response.success && response.data) {
        set({ conversations: response.data });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load conversations';
      set({ error: message });
    }
  },

  createConversation: async (title?: string) => {
    try {
      const response = await aiChatApi.createConversation(title ? { title } : undefined);
      if (response.success && response.data) {
        const newConversation = response.data;
        set((state) => ({
          conversations: [newConversation, ...state.conversations],
          currentConversationId: newConversation.id,
          messages: [],
          error: null,
        }));
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create conversation';
      set({ error: message });
    }
  },

  selectConversation: async (id: string) => {
    set({ currentConversationId: id, error: null });
    try {
      const response = await aiChatApi.getConversation(id);
      if (response.success && response.data) {
        set({ messages: response.data.messages || [] });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load conversation';
      set({ error: message });
    }
  },

  deleteConversation: async (id: string) => {
    try {
      const response = await aiChatApi.deleteConversation(id);
      if (response.success) {
        const { currentConversationId } = get();
        set((state) => ({
          conversations: state.conversations.filter((c) => c.id !== id),
          currentConversationId: currentConversationId === id ? null : currentConversationId,
          messages: currentConversationId === id ? [] : state.messages,
        }));
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete conversation';
      set({ error: message });
    }
  },

  clearCurrentConversation: () => {
    set({ currentConversationId: null, messages: [] });
  },

  sendMessage: async (content: string) => {
    const { currentConversationId } = get();

    if (!currentConversationId) {
      // Create a new conversation first, then send the message
      try {
        const createResponse = await aiChatApi.createConversation();
        if (createResponse.success && createResponse.data) {
          const newConversation = createResponse.data;
          set((state) => ({
            conversations: [newConversation, ...state.conversations],
            currentConversationId: newConversation.id,
            messages: [],
            isLoading: true,
            error: null,
          }));

          // Add user message optimistically
          const userMessage: Message = {
            id: `temp-${Date.now()}`,
            role: 'user',
            content,
            createdAt: new Date().toISOString(),
          };
          set((state) => ({ messages: [...state.messages, userMessage] }));

          // Send the message
          const sendResponse = await aiChatApi.sendMessage(newConversation.id, content);
          if (sendResponse.success && sendResponse.data) {
            // Replace optimistic user message and add AI response
            const assistantMessage: Message = sendResponse.data;
            const finalUserMessage: Message = {
              ...userMessage,
              id: userMessage.id.replace('temp-', 'user-'),
            };
            // Update conversation title if generated
            if (sendResponse.data!.title) {
              set((state) => ({
                messages: [...state.messages.filter((m) => !m.id.startsWith('temp-')), finalUserMessage, assistantMessage],
                conversations: state.conversations.map((c) =>
                  c.id === newConversation.id ? { ...c, title: sendResponse.data!.title! } : c
                ),
                isLoading: false,
              }));
            } else {
              set((state) => ({
                messages: [...state.messages.filter((m) => !m.id.startsWith('temp-')), finalUserMessage, assistantMessage],
                isLoading: false,
              }));
            }
          } else {
            set({ isLoading: false });
          }
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to send message';
        set({ isLoading: false, error: message });
      }
      return;
    }

    // Add user message optimistically
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
      isLoading: true,
      error: null,
    }));

    try {
      const response = await aiChatApi.sendMessage(currentConversationId, content);
      if (response.success && response.data) {
        const assistantMessage: Message = response.data;
        const finalUserMessage: Message = {
          ...userMessage,
          id: userMessage.id.replace('temp-', 'user-'),
        };
        // Update conversation title if generated (first message)
        if (response.data!.title) {
          set((state) => ({
            messages: [...state.messages.filter((m) => !m.id.startsWith('temp-')), finalUserMessage, assistantMessage],
            conversations: state.conversations.map((c) =>
              c.id === currentConversationId ? { ...c, title: response.data!.title! } : c
            ),
            isLoading: false,
          }));
        } else {
          set((state) => ({
            messages: [...state.messages.filter((m) => !m.id.startsWith('temp-')), finalUserMessage, assistantMessage],
            isLoading: false,
          }));
        }
      } else {
        set({ isLoading: false });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to send message';
      set({ isLoading: false, error: message });
    }
  },

  clearError: () => set({ error: null }),
}));
